import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
    Box, Chip, Paper, Table, TableBody,
    TableCell, TableHead, TableRow, Typography,
} from "@mui/material";

import { financeApi, fmtKRWFull } from "../../api/financeApi";
import { ds } from "../../../../styles/designSystem";
import { formatDate } from "../../../../util/dateUtils";
import LoadingDots from "../../../../component/common/LoadingDots";
import SearchField, { searchMatch } from "../../../../component/common/SearchField";
import ComboBox from "../../../../component/common/ComboBox";
import { useFinanceLinks } from "../../components/FinanceLinkProvider";

/* 지출요청 상태 버킷 — 부서별 화면(ExpenseRequestTab)과 같은 기준이어야 두 화면의 합계가 맞는다. */
const BUCKET_META = {
    planned:   { label: "지출예정", color: "#2563eb",                   bg: "#eff6ff" },
    confirmed: { label: "지출확정", color: ds.finance.income,           bg: "#f0fdf4" },
    held:      { label: "보류",     color: ds.finance.scheduledPayment, bg: "#fffbeb" },
    rejected:  { label: "반려",     color: ds.finance.expense,          bg: "#fef2f2" },
};
const BUCKET_ORDER = ["planned", "confirmed", "held", "rejected"];

const bucketOf = (status) => {
    switch (status) {
        case "APPROVED": return "confirmed";
        case "HELD":     return "held";
        case "REJECTED": return "rejected";
        default:         return "planned";   // PENDING, IN_PROGRESS = 결재 진행중 = 지출예정
    }
};

/** 프로젝트가 안 붙은 건들을 모으는 가상 그룹 키. 실제 채널 id 와 섞이지 않게 문자열로 둔다. */
const NONE = "none";

const projectUrl = (project) =>
    `/finance?section=expenserequestproject&tab=${encodeURIComponent("지출요청(프로젝트별)")}`
    + `&project=${project || "all"}`;

/**
 * 지출요청(프로젝트별) — 부서별 화면과 같은 결재를 <b>프로젝트 기준</b>으로 묶어 보는 조회 전용 화면.
 *
 * <h3>왜 사이드바에 프로젝트를 펼치지 않나</h3>
 * <p>부서는 열댓 개로 고정이지만 프로젝트는 계속 늘어나고 종료된 것도 남는다. 사이드바에 펼치면
 * 재무 메뉴가 통째로 프로젝트 목록이 된다. 그래서 사이드바 항목은 하나이고, 고르는 일은 이 화면 안
 * 드롭다운과 검색이 맡는다.
 *
 * <h3>«프로젝트 미연결» 을 숨기지 않는 이유</h3>
 * <p>프로젝트 선택을 필수화하기 전 문서는 이름만 적혀 있고 채널이 안 붙어 있다. 그 건들은
 * 프로젝트별 지출 취합에서 통째로 빠지는데, 문서 화면에는 프로젝트명이 멀쩡히 찍혀 있어서 아무도
 * 눈치채지 못한다. 여기서는 맨 아래 «프로젝트 미연결» 그룹으로 따로 세워 두고 적힌 이름을 함께
 * 보여 준다 — 결산 때가 아니라 지금 보이게 하는 것이 이 그룹의 목적이다.
 * (연결은 결재 &gt; 관리자 &gt; 프로젝트 연결 탭에서 한다.)
 */
export default function ExpenseRequestByProjectTab({ projectChannels = [] }) {
    const location = useLocation();
    const history  = useHistory();
    const { openApproval } = useFinanceLinks();

    const [raw, setRaw]           = useState(null);
    const [loading, setLoading]   = useState(true);
    const [bucketOn, setBucketOn] = useState(() => new Set(BUCKET_ORDER));   // 반려 포함 전체 상태 기본 표시
    const [q, setQ]               = useState("");

    // 프로젝트 선택 — URL(?project=) 이 단일 출처. 새로고침·뒤로가기에도 선택이 유지된다.
    const projectParam    = new URLSearchParams(location.search).get("project");
    const selectedProject = projectParam && projectParam !== "all" ? projectParam : "";

    useEffect(() => {
        if (projectParam == null) history.replace(projectUrl("all"));
    }, [projectParam, history]);

    useEffect(() => {
        financeApi.getFinanceApprovals()
            .then(d => setRaw(Array.isArray(d) ? d : (d || [])))
            .catch(() => setRaw([]))
            .finally(() => setLoading(false));
    }, []);

    const toggleBucket = (b) => setBucketOn(prev => {
        const next = new Set(prev);
        if (next.has(b)) next.delete(b); else next.add(b);
        return next;
    });

    // 채널 마스터의 정식 명칭 — 결재에 승격 저장된 channelName 이 옛 이름일 수 있어 이걸 우선한다.
    const channelNameById = useMemo(() => {
        const m = new Map();
        (projectChannels || []).forEach(c => m.set(String(c.id), c.name));
        return m;
    }, [projectChannels]);

    // 결재 응답 → 표 행. 프로젝트는 form(대금지급·구매품의)의 channelId/channelName 에서 읽는다.
    const rows = useMemo(() => (raw || []).map(a => {
        const fd = a.form || {};
        const channelId = fd.channelId != null ? String(fd.channelId) : null;
        return {
            id: a.id,
            typeLabel: a.typeLabel,
            title: a.title,
            amount: Number(fd.amount) || 0,
            paymentDate: fd.paymentDate || "",
            vendor: fd.vendor || fd.vendorName || "",
            submitterName: a.submitterName || "",
            deptName: a.submitterDept || "미지정",
            channelId,
            // 연결된 건은 마스터 이름, 미연결 건은 문서에 «적혀 있는» 이름 그대로.
            channelName: channelId
                ? (channelNameById.get(channelId) || fd.channelName || `채널 #${channelId}`)
                : (fd.channelName || ""),
            bucket: bucketOf(a.status),
            createdAt: a.createdAt ? formatDate(a.createdAt) : "",
        };
    }), [raw, channelNameById]);

    const filtered = useMemo(() => rows.filter(r => {
        if (!bucketOn.has(r.bucket)) return false;
        if (selectedProject === NONE) { if (r.channelId) return false; }
        else if (selectedProject)     { if (r.channelId !== selectedProject) return false; }
        return searchMatch(q, [r.title, r.vendor, r.submitterName, r.deptName, r.typeLabel, r.channelName]);
    }), [rows, bucketOn, selectedProject, q]);

    /* 프로젝트별 그룹 — 이름순, «프로젝트 미연결» 은 항상 맨 아래.
       (연결된 건들 사이에 섞이면 «빠져 있는 돈» 이라는 성격이 묻힌다) */
    const groups = useMemo(() => {
        const map = new Map();
        filtered.forEach(r => {
            const key = r.channelId || NONE;
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    linked: !!r.channelId,
                    name: r.channelId ? r.channelName : "프로젝트 미연결",
                    rows: [],
                });
            }
            map.get(key).rows.push(r);
        });
        return [...map.values()].sort((a, b) => {
            if (a.linked !== b.linked) return a.linked ? -1 : 1;
            return a.name.localeCompare(b.name, "ko");
        });
    }, [filtered]);

    // 드롭다운 — 지출요청이 실제로 있는 프로젝트만 올린다. 활성 채널 전부를 올리면 대부분이
    // «0건» 인 목록이 되어 고르기가 더 어려워진다.
    const projectOptions = useMemo(() => {
        const seen = new Map();
        rows.forEach(r => {
            if (!r.channelId) return;
            if (!seen.has(r.channelId)) seen.set(r.channelId, r.channelName);
        });
        const opts = [...seen.entries()]
            .sort((a, b) => a[1].localeCompare(b[1], "ko"))
            .map(([value, label]) => ({ value, label }));
        const unlinkedCount = rows.filter(r => !r.channelId).length;
        return [
            { value: "", label: "전체 프로젝트" },
            ...opts,
            ...(unlinkedCount ? [{ value: NONE, label: `프로젝트 미연결 (${unlinkedCount}건)` }] : []),
        ];
    }, [rows]);

    // 요약 — 지출예정 합계 · 지출확정 합계 · 표시 건수 · 미연결 건수
    const plannedTotal   = filtered.filter(r => r.bucket === "planned").reduce((s, r) => s + r.amount, 0);
    const confirmedTotal = filtered.filter(r => r.bucket === "confirmed").reduce((s, r) => s + r.amount, 0);
    const unlinkedRows   = rows.filter(r => !r.channelId && r.bucket !== "rejected");
    const unlinkedTotal  = unlinkedRows.reduce((s, r) => s + r.amount, 0);

    const onProjectChange = (v) => history.push(projectUrl(v));

    const linkedGroupCount = groups.filter(g => g.linked).length;

    return (
        <Box>
            {/* 요약 카드 */}
            <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Paper sx={{ ...ds.cardPadded, flex: 1, minWidth: 190 }}>
                    <Typography sx={ds.typography.label}>지출 예정 합계 (진행중)</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#2563eb", mt: 0.5 }}>
                        {fmtKRWFull(plannedTotal)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        {filtered.filter(r => r.bucket === "planned").length}건
                    </Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1, minWidth: 190 }}>
                    <Typography sx={ds.typography.label}>지출 확정 합계</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: ds.finance.income, mt: 0.5 }}>
                        {fmtKRWFull(confirmedTotal)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        {filtered.filter(r => r.bucket === "confirmed").length}건
                    </Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1, minWidth: 190 }}>
                    <Typography sx={ds.typography.label}>표시 건수</Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: ds.finance.balance, mt: 0.5 }}>
                        {filtered.length}건
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        {selectedProject ? "선택 프로젝트" : `${linkedGroupCount}개 프로젝트`}
                    </Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1, minWidth: 190 }}>
                    <Typography sx={ds.typography.label}>프로젝트 미연결 (반려 제외)</Typography>
                    <Typography sx={{
                        fontSize: 24, fontWeight: 800, mt: 0.5,
                        color: unlinkedRows.length ? "#b45309" : "#94a3b8",
                    }}>
                        {fmtKRWFull(unlinkedTotal)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        {unlinkedRows.length}건 · 프로젝트별 취합에서 빠짐
                    </Typography>
                </Paper>
            </Box>

            {/* 미연결이 있을 때만 — 어디서 고치는지까지 알려 준다. 숫자만 보여 주면 손쓸 길이 없다. */}
            {/*{unlinkedRows.length > 0 && (*/}
            {/*    <Box sx={{*/}
            {/*        bgcolor: "#fffbeb", border: "1px solid #fde68a", borderRadius: 1,*/}
            {/*        px: 1.5, py: 1.25, mb: 2,*/}
            {/*    }}>*/}
            {/*        <Typography sx={{ fontSize: 12, color: "#92400e", lineHeight: 1.7 }}>*/}
            {/*            프로젝트 이름만 적혀 있고 실제 프로젝트에 연결되지 않은 지출요청이 <b>{unlinkedRows.length}건</b> 있습니다.*/}
            {/*            연결하기 전까지 <b>프로젝트별 손익·취합에서 계속 빠집니다.</b><br />*/}
            {/*            연결은 <b>결재 &gt; 관리자 &gt; 프로젝트 연결</b> 탭에서 합니다.*/}
            {/*        </Typography>*/}
            {/*    </Box>*/}
            {/*)}*/}

            {/* 상태 필터 칩 + 프로젝트 선택 + 검색 */}
            <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
                {BUCKET_ORDER.map(b => {
                    const m = BUCKET_META[b];
                    const on = bucketOn.has(b);
                    return (
                        <Chip key={b} label={m.label} size="small" onClick={() => toggleBucket(b)}
                            sx={{
                                cursor: "pointer", fontWeight: 700, fontSize: 11.5, height: 24,
                                bgcolor: on ? m.bg : "#f8fafc",
                                color: on ? m.color : "#cbd5e1",
                                border: `1px solid ${on ? `${m.color}44` : "#e2e8f0"}`,
                            }} />
                    );
                })}
                <Box sx={{ width: 240 }}>
                    <ComboBox placeholder="전체 프로젝트" clearable={false}
                        options={projectOptions}
                        value={selectedProject}
                        onChange={onProjectChange} />
                </Box>
                <Box sx={{ flex: 1 }} />
                <SearchField value={q} onChange={setQ} placeholder="프로젝트·내용·거래처·기안자·부서 검색" />
            </Box>

            {/* 표 — 프로젝트별 그룹 + 소계 */}
            <Paper sx={ds.card}>
                {loading && <LoadingDots />}
                {!loading && (
                    <Table size="small" sx={ds.tableSx}>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>기안일</TableCell>
                                <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>유형</TableCell>
                                <TableCell>내용</TableCell>
                                <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>거래처</TableCell>
                                <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>기안자</TableCell>
                                <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>부서</TableCell>
                                <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>지급예정일</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>금액</TableCell>
                                <TableCell align="center">상태</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                                        해당 조건의 지출요청이 없습니다.
                                    </TableCell>
                                </TableRow>
                            ) : groups.map(g => {
                                // 소계는 반려를 뺀다 — 반려는 실제로 나갈 돈이 아니다(총합계와 같은 기준).
                                const subtotal = g.rows.filter(r => r.bucket !== "rejected").reduce((s, r) => s + r.amount, 0);
                                return (
                                    <React.Fragment key={g.key}>
                                        <TableRow sx={{ bgcolor: g.linked ? "#f1f5f9" : "#fffbeb" }}>
                                            <TableCell colSpan={9} sx={{
                                                fontWeight: 800, fontSize: 12.5,
                                                color: g.linked ? "#334155" : "#92400e",
                                            }}>
                                                {g.name}
                                                <Box component="span" sx={{ ml: 1, fontWeight: 600, color: "#94a3b8" }}>
                                                    {g.rows.length}건 · {fmtKRWFull(subtotal)}
                                                </Box>
                                                {!g.linked && (
                                                    <Box component="span" sx={{ ml: 1, fontWeight: 700, fontSize: 11, color: "#b45309" }}>
                                                        · 프로젝트별 취합에 잡히지 않음
                                                    </Box>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        {g.rows.map(r => {
                                            const m = BUCKET_META[r.bucket];
                                            return (
                                                <TableRow key={r.id} hover onClick={() => openApproval(r.id)} sx={{ cursor: "pointer" }}>
                                                    <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 12 }}>{r.createdAt || "—"}</TableCell>
                                                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                                                        <Chip label={r.typeLabel} size="small"
                                                            sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: "#eef2ff", color: "#4f46e5" }} />
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ maxWidth: 280 }}>
                                                        <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }} title={r.title}>{r.title}</Typography>
                                                        {/* 미연결 그룹에서는 문서에 적힌 이름이 어느 프로젝트인지 판단할 유일한 단서다 */}
                                                        {!g.linked && r.channelName && (
                                                            <Typography noWrap sx={{ fontSize: 11, color: "#b45309", fontWeight: 600 }}>
                                                                적힌 이름: {r.channelName}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 12 }}>{r.vendor || "—"}</TableCell>
                                                    <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 12, color: "#475569" }}>{r.submitterName || "—"}</TableCell>
                                                    <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 12, color: "#475569" }}>{r.deptName}</TableCell>
                                                    <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 12 }}>{r.paymentDate || "—"}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, color: ds.finance.expense, whiteSpace: "nowrap" }}>
                                                        −{fmtKRWFull(r.amount)}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={m.label} size="small"
                                                            sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: m.bg, color: m.color }} />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        <TableRow>
                                            <TableCell colSpan={7} align="right" sx={{ fontWeight: 700, color: "#64748b", fontSize: 12 }}>
                                                {g.name} 소계 (반려 제외)
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800, color: ds.finance.expense, whiteSpace: "nowrap" }}>
                                                −{fmtKRWFull(subtotal)}
                                            </TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </React.Fragment>
                                );
                            })}
                            {/* 총 합계 */}
                            {filtered.length > 0 && (
                                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                                    <TableCell colSpan={7} sx={{ fontWeight: 800 }}>
                                        총 합계 ({filtered.filter(r => r.bucket !== "rejected").length}건 · 반려 제외)
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 800, color: ds.finance.expense, whiteSpace: "nowrap" }}>
                                        −{fmtKRWFull(filtered.filter(r => r.bucket !== "rejected").reduce((s, r) => s + r.amount, 0))}
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </Paper>
        </Box>
    );
}
